import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SmoothMarkdown } from '@/components/SmoothMarkdown';
import { Textarea } from '@/components/ui/textarea';
import { NarrativeExampleActions } from '@/features/profile/components/NarrativeExampleActions';
import { NarrativeExampleSkeleton } from '@/features/profile/components/NarrativeExampleSkeleton';
import { cn } from '@/utils/utils';

interface NarrativeExampleProps {
  className?: string;
  content?: string;
  isLoading?: boolean;
  onEdit?: () => void;
  onRefresh?: () => void;
  onSave?: (content: string) => void;
}

const visibilityClassName = (isRegenerating: boolean, isVisible: boolean) =>
  isRegenerating
    ? 'opacity-45 saturate-50'
    : isVisible
      ? 'opacity-100 saturate-100'
      : 'opacity-0 saturate-0';

export function NarrativeExample({
  className,
  content = '',
  isLoading = false,
  onEdit,
  onRefresh,
  onSave,
}: NarrativeExampleProps) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [hasContent, setHasContent] = useState(!!content);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const previousExpandedStateRef = useRef(false);
  const wasLoadingRef = useRef(isLoading);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    if (isLoading && !wasLoadingRef.current) {
      // oxlint-disable-next-line react-hooks-js/set-state-in-effect -- syncs loading transition into animation state
      setIsVisible(false);
      setHasContent(false);
      setIsRegenerating(false);
    } else if (wasLoadingRef.current && !isLoading && content) {
      // oxlint-disable-next-line react-hooks-js/set-state-in-effect -- syncs loading transition into animation state
      setIsVisible(true);
      setHasContent(true);
      timer = setTimeout(() => setIsRegenerating(false), 300);
    }
    wasLoadingRef.current = isLoading;
    return () => {
      if (timer !== undefined) {
        clearTimeout(timer);
      }
    };
  }, [isLoading, content]);

  // Show skeleton on initial load, only hide during regenerate
  const shouldShowSkeleton = isLoading && !hasContent;

  const handleRefresh = () => {
    setIsRegenerating(true);
    setIsVisible(false);
    if (onRefresh) {
      onRefresh();
    }
  };

  const getEffectiveContent = (value: string) =>
    value && value.length > 0 ? value : t('profile.example.defaultContent');

  const effectiveContent = getEffectiveContent(content);
  const [editedContent, setEditedContent] = useState(effectiveContent);
  const displayContent = effectiveContent; // Always show full content with scrolling

  const handleEditClick = () => {
    // Store current expanded state and auto-expand
    previousExpandedStateRef.current = isExpanded;
    setEditedContent(effectiveContent);
    setIsEditing(true);
    setIsExpanded(true); // Auto-expand for editing
    // Scroll into view with offset to ensure edit area is visible
    if (containerRef.current) {
      setTimeout(() => {
        containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 0);
    }
    if (onEdit) {
      onEdit();
    }
  };

  const handleSave = () => {
    setIsEditing(false);
    // Restore previous expanded state
    setIsExpanded(previousExpandedStateRef.current);
    if (onSave) {
      onSave(editedContent);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    // Restore previous expanded state
    setIsExpanded(previousExpandedStateRef.current);
  };

  // Show skeleton on initial load when no content yet
  if (shouldShowSkeleton) {
    return <NarrativeExampleSkeleton className={className} />;
  }

  return (
    <div
      className={cn(
        'flex w-full shrink-0 flex-col overflow-hidden rounded-[16px] border border-border-subtle bg-back-bg2 p-3 shadow-none transition-[max-height,opacity,saturate] duration-300 ease-out',
        isExpanded || isEditing
          ? 'max-h-[calc(100vh-120px)] min-h-[calc(100vh-120px)]'
          : 'max-h-[280px]',
        visibilityClassName(isRegenerating, isVisible),
        className,
      )}
      ref={containerRef}
    >
      {/* Header - Never scrolls, always visible */}
      <div className="mb-2 flex shrink-0 items-center justify-between">
        <h3 className="text-foreground font-['Inter'] text-[12px] leading-[160%] font-semibold tracking-normal">
          {t('profile.example.title')}
        </h3>
        <div className="flex items-center gap-1.5">
          <NarrativeExampleActions
            isEditing={isEditing}
            isExpanded={isExpanded}
            isRegenerating={isRegenerating}
            onCancel={handleCancel}
            onEditClick={onEdit && handleEditClick}
            onRefreshClick={onRefresh && handleRefresh}
            onSave={handleSave}
            onToggleExpanded={() => setIsExpanded(!isExpanded)}
          />
        </div>
      </div>

      {/* Content - Scrollable area */}
      <div className={cn('flex min-h-0 flex-1 flex-col overflow-y-auto')}>
        {isEditing ? (
          <>
            <Textarea
              autoFocus
              className="custom-scrollbar border-border-subtle bg-back-bg text-secondary focus:border-border-subtle min-h-0 flex-1 resize-none overflow-y-auto rounded-[6px] border-[0.5px] border-solid p-3 font-['Inter'] text-[12px] leading-[170%] font-normal tracking-[0%] outline-none focus:shadow-none focus:ring-0 focus:outline-none focus-visible:ring-0"
              onChange={(e) => setEditedContent(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && e.ctrlKey) {
                  e.preventDefault();
                  handleSave();
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  handleCancel();
                }
              }}
              value={editedContent}
            />
          </>
        ) : (
          <div className="custom-scrollbar text-foreground [&_blockquote]:border-border-subtle [&_code]:bg-back-bg2 [&_pre]:bg-back-bg2 min-h-0 flex-1 overflow-y-auto font-['Inter'] text-[12px] leading-[160%] font-normal tracking-normal [&_blockquote]:my-1.5 [&_blockquote]:border-l-2 [&_blockquote]:pl-2 [&_code]:rounded [&_code]:px-1 [&_code]:py-0.5 [&_em]:italic [&_h1]:mt-1.5 [&_h1]:mb-1 [&_h1]:text-[12px] [&_h1]:font-semibold [&_h2]:mt-1.5 [&_h2]:mb-1 [&_h2]:text-[12px] [&_h2]:font-semibold [&_h3]:mt-1.5 [&_h3]:mb-1 [&_h3]:text-[12px] [&_h3]:font-medium [&_li]:mb-0.5 [&_ol]:mt-1 [&_ol]:mb-1 [&_ol]:list-decimal [&_ol]:pl-4 [&_p]:mb-1 [&_p]:whitespace-pre-wrap [&_pre]:my-2 [&_pre]:overflow-x-auto [&_pre]:rounded [&_pre]:p-2 [&_strong]:font-semibold [&_ul]:mt-1 [&_ul]:mb-1 [&_ul]:list-disc [&_ul]:pl-4">
            <SmoothMarkdown content={displayContent} />
          </div>
        )}
      </div>
    </div>
  );
}
